import { useState, useEffect } from "react";
import axios from "axios";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Users, ChevronDown, Check, ArrowLeft, Search } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function RoleSwitcher() {
  const { user } = useAuth();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [impersonating, setImpersonating] = useState(!!localStorage.getItem("eagle_admin_original_token"));

  // Debounced search
  useEffect(() => {
    if (user?.role !== "admin" || impersonating) return;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${BACKEND_URL}/api/auth/searchable-users?q=${encodeURIComponent(query)}`,
          { withCredentials: true },
        );
        setResults(res.data || []);
      } catch (e) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [query, user, impersonating]);

  if (!user) return null;

  // If admin is currently impersonating, show a "return to admin" button
  if (impersonating) {
    const returnToAdmin = async () => {
      const originalToken = localStorage.getItem("eagle_admin_original_token");
      if (!originalToken) {
        localStorage.removeItem("eagle_admin_original_token");
        window.location.href = "/login";
        return;
      }
      try {
        await axios.post(
          `${BACKEND_URL}/api/auth/end-impersonation`,
          { admin_session_token: originalToken },
          { withCredentials: true },
        );
        localStorage.removeItem("eagle_admin_original_token");
        window.location.href = "/";
      } catch (e) {
        localStorage.removeItem("eagle_admin_original_token");
        window.location.href = "/login";
      }
    };
    return (
      <button
        onClick={returnToAdmin}
        className="w-full mb-3 text-xs px-3 py-2 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-900 border border-amber-300 flex items-center justify-center gap-2 font-medium"
        title="Return to your admin account"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        Return to Admin
      </button>
    );
  }

  // Only admins can preview other users
  if (user.role !== "admin") return null;

  const impersonate = async (target) => {
    try {
      const payload = target.user_id
        ? { user_id: target.user_id }
        : { student_id: target.student_id };
      const res = await axios.post(`${BACKEND_URL}/api/auth/impersonate`, payload, {
        withCredentials: true,
      });
      if (res.data?.admin_session_token) {
        localStorage.setItem("eagle_admin_original_token", res.data.admin_session_token);
      }
      window.location.href = "/";
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not impersonate that user.");
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="w-full mb-3 text-xs px-3 py-2 rounded-lg bg-white hover:bg-slate-50 text-slate-700 flex items-center justify-between gap-2 border border-slate-200 shadow-sm">
          <span className="flex items-center gap-2">
            <Users className="w-3.5 h-3.5 text-blue-600" />
            <span className="font-medium">View as:</span>
            <span className="text-slate-900">Administrator</span>
          </span>
          <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-80 p-0">
        <DropdownMenuLabel className="text-xs text-slate-500 font-normal px-3 pt-3">
          Preview as a specific user
        </DropdownMenuLabel>
        <div className="p-2 pt-1">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search by name, email, or student ID..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-8 h-9 text-sm"
              autoFocus
            />
          </div>
        </div>
        <DropdownMenuSeparator className="my-0" />
        <div className="max-h-72 overflow-y-auto">
          {loading && <div className="px-3 py-3 text-xs text-slate-500">Searching...</div>}
          {!loading && results.length === 0 && (
            <div className="px-3 py-3 text-xs text-slate-500 italic">
              {query ? "No matches found." : "Type to search users..."}
            </div>
          )}
          {!loading &&
            results.map((r, i) => (
              <DropdownMenuItem
                key={r.user_id || r.student_id || i}
                onClick={() => impersonate(r)}
                className="cursor-pointer flex items-center justify-between gap-2 px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">
                    {r.name || r.email || r.student_id}
                  </p>
                  <p className="text-xs text-slate-500 truncate">
                    {r.type === "student"
                      ? `Student · ID ${r.student_id}${r.grade_level ? ` · G${r.grade_level}` : ""}`
                      : `${r.role === "admin" ? "Admin" : r.user_type || "Staff"} · ${r.email}`}
                  </p>
                </div>
                <Check className="w-4 h-4 text-slate-300" />
              </DropdownMenuItem>
            ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
